import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Chart } from "@antv/g2";
import { observer } from "mobx-react";
import { useEffect, useMemo, useRef } from "react";
import { useStores } from "../../../../../../store/stores.js";
import { COLOR_SUCCESS_SOFT, antdTheme } from "../../../../../../style/antdTheme.js";
import { formatNumber } from "../../../../../../utils/getDecimalSeparator.js";
import { __ } from "../../../../../../utils/i18n.js";
// Chart is built once on mount; tokens are read statically here (NOT via the
// useToken() hook) because the @antv/g2 v4 instance does not re-render on theme
// change. Per-series colours below follow the V2 token mapping.
const TOKEN = antdTheme.token;
const SERIES_COLORS = {
    netCommissionLine: TOKEN.colorPrimary,
    cumulative: TOKEN.colorSuccess,
    cumulativeAreaLight: COLOR_SUCCESS_SOFT,
    salesBar: TOKEN.colorSuccess,
    couponBar: TOKEN.colorWarning,
    conversionLine: TOKEN.colorPrimary,
    clicksLine: TOKEN.colorTextSecondary
};
const PADDING = {
    left: 10,
    right: 10
};
const TransactionsChart = observer(()=>{
    const ref = useRef();
    const { accountV2Store: { session } } = useStores();
    const statistics = session?.affiliate?.statistics ?? [];
    const summarizedData = useMemo(()=>{
        const result = {
            sales: 0,
            affiliateCouponUsages: 0,
            netCommissionValue: 0,
            affiliateLinkClicks: 0,
            // Average conversion rate must ignore zero-click days: with zero clicks the
            // per-day rate is forced to 0 for the two-dimensional graph, but feeding those
            // zeros into the average would bias it downward. So only days with a rate > 0
            // contribute to the divisor.
            avgConversionRate: 0,
            conversationRates: []
        };
        for (const { name, value } of statistics){
            if (name === "conversationRate") {
                if (+value > 0) {
                    result.conversationRates.push(+value);
                    result.avgConversionRate += +value;
                }
            } else {
                result[name] += +value;
            }
        }
        result.avgConversionRate = result.avgConversionRate / (result.conversationRates.length || 1);
        return result;
    }, [
        statistics
    ]);
    useEffect(()=>{
        if (ref.current) {
            const data = statistics;
            // Initiate chart
            const chart = new Chart({
                container: ref.current,
                autoFit: true,
                height: 340
            }).animate(false).tooltip({
                showCrosshairs: true
            }).legend(false).scale({
                date: {
                    sync: true,
                    range: [
                        0,
                        1
                    ]
                },
                value: {
                    sync: false
                }
            });
            chart.removeInteraction("tooltip");
            // Helper functions and constants
            const viewAnnotationText = (view, content)=>{
                view.annotation().text({
                    top: true,
                    offsetY: -20,
                    offsetX: 6,
                    style: {
                        fontSize: 14,
                        fill: "#00000073"
                    },
                    position: [
                        "min",
                        "max"
                    ],
                    content
                });
            };
            /**
             * ===============
             * Net commission
             * ===============
             */ const view1 = chart.createView({
                region: {
                    start: {
                        x: 0,
                        y: 0
                    },
                    end: {
                        x: 1,
                        y: 0.55
                    }
                },
                padding: [
                    25,
                    PADDING.right,
                    60,
                    PADDING.left
                ]
            }).data(data.filter(({ name })=>name === "netCommissionValue").map(({ date, name, value }, i, arr)=>({
                    index: i,
                    date,
                    name,
                    value,
                    cumulates: i === 0 ? value : +value + arr.slice(0, i).reduce((p, { value: c })=>p + +c, 0)
                }))).tooltip({
                shared: true,
                showMarkers: true,
                customItems: (items)=>[
                        {
                            ...items[0],
                            color: SERIES_COLORS.netCommissionLine,
                            name: __("Commissions"),
                            value: `+ ${formatNumber(items[0].data.value, 2)} €`
                        },
                        {
                            ...items[0],
                            color: SERIES_COLORS.cumulative,
                            name: __("Commutated commissions"),
                            value: `${formatNumber(items[0].data.cumulates, 2)} €`
                        }
                    ]
            }).interaction("tooltip").interaction("sibling-tooltip").axis("date", false).axis("cumulates", {
                label: null
            }).axis("value", {
                line: null,
                tickLine: null,
                position: "right",
                top: true,
                grid: {
                    line: null
                },
                label: null
            }).scale({
                cumulates: {},
                value: {
                    range: [
                        0,
                        0.3
                    ]
                }
            });
            view1.area().position("date*cumulates").color(`l(90) 0.8:${SERIES_COLORS.cumulative} 1.0:${SERIES_COLORS.cumulativeAreaLight}`);
            view1.line().position("date*cumulates").color(SERIES_COLORS.cumulative).style({
                lineWidth: 1
            });
            view1.line().position("date*value").color(SERIES_COLORS.netCommissionLine).shape("smooth");
            viewAnnotationText(view1, __("Net commissions growth"));
            /**
             * ===============
             * Sales
             * ===============
             */ const view2 = chart.createView({
                region: {
                    start: {
                        x: 0,
                        y: 0.55
                    },
                    end: {
                        x: 1,
                        y: 0.75
                    }
                },
                padding: [
                    0,
                    PADDING.right,
                    60,
                    PADDING.left
                ]
            }).data(data).filter("name", (v)=>[
                    "sales",
                    "affiliateCouponUsages"
                ].indexOf(v) > -1).tooltip({
                shared: true,
                showMarkers: true,
                showTitle: false,
                customItems: (items)=>{
                    for (const item of items){
                        Object.assign(item, {
                            name: item.name === "sales" ? __("Sales") : __("Coupon used")
                        });
                    }
                    return items;
                }
            }).interaction("tooltip").interaction("sibling-tooltip").axis("date", {
                label: null
            }).axis("value", {
                label: null,
                grid: {
                    line: null
                }
            });
            view2.interval().position("date*value").color("name", (name)=>name === "sales" ? SERIES_COLORS.salesBar : SERIES_COLORS.couponBar);
            viewAnnotationText(view2, __("Sales"));
            /**
             * ===============
             * Conversion
             * ===============
             */ const view3 = chart.createView({
                region: {
                    start: {
                        x: 0,
                        y: 0.75
                    },
                    end: {
                        x: 1,
                        y: 1
                    }
                },
                padding: [
                    0,
                    PADDING.right,
                    70,
                    PADDING.left
                ]
            }).interaction("tooltip").interaction("sibling-tooltip").data(data.filter(({ name })=>name === "affiliateLinkClicks").map(({ value, name, date })=>{
                return {
                    value,
                    name,
                    date,
                    conversationRate: data.filter(({ date: cDate, name })=>cDate === date && name === "conversationRate")[0]?.value
                };
            })).tooltip({
                shared: true,
                showMarkers: true,
                showTitle: false,
                customItems: (items)=>{
                    for (const item of items){
                        Object.assign(item, {
                            name: item.name === "conversationRate" ? __("Conversion rate") : __("Link clicks"),
                            value: item.name === "conversationRate" ? `${item.value} %` : item.value
                        });
                    }
                    return items;
                }
            })// `offsetY` is a runtime-supported AxisCfg option but missing from @antv/g2 v4's typings.
            .axis("date", {
                offsetY: 20
            }).axis("conversationRate", {
                label: null,
                grid: {
                    line: null
                }
            }).axis("value", {
                line: null,
                label: null,
                tickLine: null,
                position: "right",
                grid: {
                    line: null
                }
            }).scale("conversationRate", {
                max: 100
            });
            view3.line().position("date*conversationRate").color(SERIES_COLORS.conversionLine).shape("smooth").style({
                lineWidth: 2
            });
            view3.line().position("date*value").shape("smooth").color(SERIES_COLORS.clicksLine).style({
                lineWidth: 1
            });
            viewAnnotationText(view3, __("Conversion rate and link clicks"));
            let timeout;
            chart.render();
            timeout = setTimeout(()=>{
                // Sometimes, the chart does not render immediate, so we need to simulate a resize event
                window.dispatchEvent(new Event("resize"));
                timeout = setTimeout(()=>{
                    ref.current.style.opacity = "1";
                }, 500);
            }, 0);
            return ()=>{
                clearTimeout(timeout);
                chart.destroy();
            };
        }
        return ()=>{
        // Silence is golden.
        };
    }, [
        ref,
        statistics
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-affiliate-metrics",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-metric",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__label",
                                children: __("Net commissions")
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "rc-account-v2-affiliate-metric__value",
                                children: [
                                    formatNumber(summarizedData.netCommissionValue, 2),
                                    " EUR"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-metric",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__label",
                                children: __("Sales")
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__value",
                                children: summarizedData.sales
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-metric",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__label",
                                children: __("Coupon usages")
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__value",
                                children: summarizedData.affiliateCouponUsages
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-metric",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__label",
                                children: __("Clicks on links")
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__value",
                                children: summarizedData.affiliateLinkClicks
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-metric",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__label",
                                children: __("Avg. conversion rate")
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-affiliate-metric__value",
                                children: `Ø ${formatNumber(summarizedData.avgConversionRate, 2)} %`
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: ref,
                style: {
                    opacity: 0,
                    transition: "opacity 250ms"
                }
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "rc-account-v2-affiliate-chart__note",
                children: __("Conversion rate excludes days with no clicks.")
            })
        ]
    });
});
export { TransactionsChart };
